import type {
  ForgeCheck,
  ForgeComment,
  ForgePullRequest,
  ForgePullRequestSummary,
  ProviderInput,
  ProviderOperation,
  ProviderOutput,
} from '@crowdex/provider-contracts';
import type { PullRequestChecksState, PullRequestSummary } from '@crowdex/workspace-protocol';
import type {
  PullRequestCockpit,
  PullRequestFeedback,
  PullRequestMutation,
  PullRequestReactionContent,
  PullRequestReviewComment,
} from '@crowdex/workspace-protocol/pull-request-cockpit';

/**
 * The code forge of a repository, as Crowdex shows and changes its pull requests: `forge.v1`
 * operations of the user's provider for the repository's host (GitHub, GitLab…), wherever they
 * are called from (the CLI through its environment, the web server itself).
 */
export type ForgeCall = <O extends ProviderOperation<'forge.v1'>>(
  operation: O,
  input: ProviderInput<'forge.v1', O>,
) => Promise<ProviderOutput<'forge.v1', O>>;

/** The host of a git remote (`git@github.com:a/b.git` → `github.com`), or null. */
export function forgeHostOfRemote(remote: string | null | undefined): string | null {
  if (!remote) return null;
  const scp = /^[^@/\s]+@([^:/\s]+):/.exec(remote.trim());
  if (scp?.[1]) return scp[1].toLowerCase();
  try {
    const url = new URL(remote.trim());
    return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'ssh:'
      ? url.hostname.toLowerCase()
      : null;
  } catch {
    return null;
  }
}

/** A pull request as git status and lists summarize it. */
export function forgePullRequestSummary(
  pull: ForgePullRequestSummary &
    Partial<
      Pick<ForgePullRequest, 'body' | 'mergeable' | 'reviewDecision' | 'additions' | 'deletions'>
    >,
): PullRequestSummary {
  return {
    url: pull.url,
    number: pull.number,
    title: pull.title,
    body: pull.body ?? '',
    state: pull.state,
    ...(pull.mergeable === undefined ? {} : { mergeability: pull.mergeable }),
    ...(pull.reviewDecision === undefined ? {} : { reviewDecision: pull.reviewDecision }),
    ...(pull.additions === undefined ? {} : { additions: pull.additions }),
    ...(pull.deletions === undefined ? {} : { deletions: pull.deletions }),
    baseBranch: pull.baseBranch,
    headBranch: pull.headBranch,
    updatedAt: pull.updatedAt,
  };
}

const FAILED_CONCLUSIONS = new Set(['failure', 'timed_out', 'action_required']);

/** Where a head's checks stand: one failed, else some still run, else all passed; none: null. */
export function forgeChecksState(
  checks: readonly Pick<ForgeCheck, 'status' | 'conclusion'>[],
): PullRequestChecksState | null {
  if (checks.length === 0) return null;
  if (checks.some((check) => FAILED_CONCLUSIONS.has(check.conclusion ?? ''))) return 'failing';
  if (checks.some((check) => check.status !== 'completed' || check.conclusion === null))
    return 'pending';
  return 'passing';
}

/**
 * A pull request's summary with its checks on its head (an open or draft one's), as the sidebar
 * marks it; without them when the forge cannot list them.
 */
export async function forgePullRequestSummaryWithChecks(
  call: ForgeCall,
  repository: string,
  pull: Parameters<typeof forgePullRequestSummary>[0],
): Promise<PullRequestSummary> {
  const summary = forgePullRequestSummary(pull);
  if (summary.state !== 'open' && summary.state !== 'draft') return summary;
  try {
    const { checks } = await call('checks.list', { repository, ref: pull.headSha });
    const state = forgeChecksState(checks);
    return state === null ? summary : { ...summary, checks: state };
  } catch {
    return summary;
  }
}

function feedbackOf(comment: ForgeComment): PullRequestFeedback {
  return {
    id: comment.id,
    kind: comment.kind,
    author: comment.author,
    body: comment.body,
    state: comment.state,
    path: comment.path,
    line: comment.line,
    diffHunk: comment.diffHunk,
    url: comment.url,
    createdAt: comment.createdAt,
    reactions: comment.reactions,
  };
}

/** A pull request in full (its checks, reviews, threads and comments), as its panel shows it. */
export async function forgeCockpit(
  call: ForgeCall,
  repository: string,
  which: { number: number } | { head: string },
): Promise<PullRequestCockpit | null> {
  const { pullRequest } = await call('pullRequest.get', { repository, ...which });
  if (pullRequest === null) return null;
  const pull = { repository, number: pullRequest.number };
  const [{ checks }, { reviews, threads }, { comments }] = await Promise.all([
    call('checks.list', { repository, ref: pullRequest.headSha }),
    call('reviews.list', pull),
    call('comments.list', pull),
  ]);
  const threaded = new Set(
    threads.flatMap((thread) => thread.comments.map((comment) => comment.id)),
  );
  return {
    pullRequest: forgePullRequestSummary(pullRequest),
    author: pullRequest.author,
    headSha: pullRequest.headSha,
    baseSha: pullRequest.baseSha,
    mergeable: pullRequest.mergeable,
    autoMergeEnabled: pullRequest.autoMergeEnabled,
    reviewDecision: pullRequest.reviewDecision,
    additions: pullRequest.additions,
    deletions: pullRequest.deletions,
    changedFiles: pullRequest.changedFiles,
    commits: pullRequest.commits.length,
    commitItems: pullRequest.commits,
    labels: pullRequest.labels,
    requestedReviewers: pullRequest.requestedReviewers,
    checks: checks.map((check) => ({
      ...check,
      conclusion: (check.conclusion as PullRequestCockpit['checks'][number]['conclusion']) ?? null,
      title: null,
    })),
    feedback: [...reviews, ...comments.filter((comment) => !threaded.has(comment.id))]
      .map(feedbackOf)
      .sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt))),
    reviewThreads: threads.map((thread) => ({
      ...thread,
      comments: thread.comments.map(
        (comment): PullRequestReviewComment => ({
          ...feedbackOf(comment),
          kind: 'inline',
          replyToId: comment.replyToId,
        }),
      ),
    })),
    reactions: pullRequest.reactions,
  };
}

const REACTIONS: Record<
  PullRequestReactionContent,
  ProviderInput<'forge.v1', 'comments.react'>['reaction']
> = {
  THUMBS_UP: '+1',
  THUMBS_DOWN: '-1',
  LAUGH: 'laugh',
  HOORAY: 'hooray',
  CONFUSED: 'confused',
  HEART: 'heart',
  ROCKET: 'rocket',
  EYES: 'eyes',
};

/** One of the panel's actions on a pull request, as forge operations. */
export async function forgeMutate(
  call: ForgeCall,
  repository: string,
  number: number,
  mutation: PullRequestMutation,
): Promise<void> {
  const pull = { repository, number };
  const expectedHeadSha = mutation.expectedHeadSha;
  switch (mutation.action) {
    case 'edit':
      await call('pullRequest.update', {
        ...pull,
        expectedHeadSha,
        ...(mutation.title === undefined ? {} : { title: mutation.title }),
        ...(mutation.body === undefined ? {} : { body: mutation.body }),
      });
      return;
    case 'close':
      await call('pullRequest.close', pull);
      return;
    case 'reopen':
      await call('pullRequest.update', { ...pull, state: 'open' });
      return;
    case 'draft':
    case 'ready':
      await call('pullRequest.update', {
        ...pull,
        expectedHeadSha,
        draft: mutation.action === 'draft',
      });
      return;
    case 'request_reviewers':
      if (!mutation.reviewers?.length) throw new Error('At least one reviewer is required.');
      await call('pullRequest.update', { ...pull, reviewers: mutation.reviewers });
      return;
    case 'enable_auto_merge':
      await call('pullRequest.update', {
        ...pull,
        expectedHeadSha,
        autoMerge: { method: mutation.method ?? 'squash' },
      });
      return;
    case 'disable_auto_merge':
      await call('pullRequest.update', { ...pull, autoMerge: false });
      return;
    case 'update_branch':
      await call('pullRequest.update', { ...pull, expectedHeadSha, updateBranch: true });
      return;
    case 'merge': {
      const merged = await call('pullRequest.merge', {
        ...pull,
        method: mutation.method ?? 'squash',
        expectedHeadSha,
      });
      if (!merged.merged) throw new Error('The forge did not merge the pull request.');
      return;
    }
    case 'comment':
      if (!mutation.body?.trim()) throw new Error('A comment is required.');
      await call('comments.create', { ...pull, body: mutation.body });
      return;
    case 'reply_thread':
      if (!mutation.threadId || !mutation.body?.trim()) throw new Error('A reply is required.');
      await call('comments.create', { ...pull, body: mutation.body, threadId: mutation.threadId });
      return;
    case 'resolve_thread':
      if (!mutation.threadId) throw new Error('A review thread is required.');
      await call('reviews.resolve', { ...pull, threadId: mutation.threadId, resolved: true });
      return;
    case 'react_comment':
      if (!mutation.commentId || !mutation.reaction)
        throw new Error('A valid comment reaction is required.');
      await call('comments.react', {
        ...pull,
        commentId: mutation.commentId,
        reaction: REACTIONS[mutation.reaction],
      });
      return;
    case 'submit_review':
      await call('reviews.create', {
        ...pull,
        decision: mutation.decision ?? 'comment',
        body: mutation.body ?? '',
        expectedHeadSha,
      });
      return;
  }
}

/** The forge of a host (`github.com`), or null when this environment cannot reach one. */
export type ForgeResolver = (host: string) => ForgeCall | null;

/** The forge and repository of a git remote, or null when it has none. */
export async function forgeOfRemote(
  resolve: ForgeResolver,
  remote: string,
): Promise<{ call: ForgeCall; repository: string; defaultBranch: string } | null> {
  const host = forgeHostOfRemote(remote);
  const call = host === null ? null : resolve(host);
  if (call === null) return null;
  const { repository } = await call('repository.resolve', { remote });
  return repository === null
    ? null
    : { call, repository: repository.fullName, defaultBranch: repository.defaultBranch };
}

/** What git status shows of a branch's pull request on its remote's forge. */
export async function forgePullRequestStatus(
  resolve: ForgeResolver | undefined,
  remoteUrl: string | null,
  branch: string,
): Promise<{
  pullRequestUrl: string | null;
  pullRequest: PullRequestSummary | null;
  pullRequestAvailability:
    | 'available'
    | 'no-remote'
    | 'unsupported-remote'
    | 'provider-unavailable';
}> {
  const none = { pullRequestUrl: null, pullRequest: null };
  if (remoteUrl === null) return { ...none, pullRequestAvailability: 'no-remote' };
  const host = forgeHostOfRemote(remoteUrl);
  const call = host === null || resolve === undefined ? null : resolve(host);
  if (call === null) return { ...none, pullRequestAvailability: 'unsupported-remote' };
  try {
    const { repository } = await call('repository.resolve', { remote: remoteUrl });
    if (repository === null) return { ...none, pullRequestAvailability: 'provider-unavailable' };
    if (branch === '') return { ...none, pullRequestAvailability: 'available' };
    const { pullRequest } = await call('pullRequest.get', {
      repository: repository.fullName,
      head: branch,
    });
    const summary =
      pullRequest === null
        ? null
        : await forgePullRequestSummaryWithChecks(call, repository.fullName, pullRequest);
    return {
      pullRequestUrl: summary?.url ?? null,
      pullRequest: summary,
      pullRequestAvailability: 'available',
    };
  } catch {
    // No provider for the host, no connection, or the forge is down: actions are unavailable.
    return { ...none, pullRequestAvailability: 'provider-unavailable' };
  }
}
